import { isPersistentStorageAvailable } from "./safeStorage";
import { i18n } from "../../i18n";

// This module is called from plain functions, not components, so it uses the
// i18next singleton rather than the useTranslation hook.
const t = (key: string, vars?: Record<string, unknown>): string => i18n.t(key, vars) as string;

export type SilentFailureStage = "cache" | "sso" | "msal";

interface SilentFailure {
  stage: SilentFailureStage;
  code?: number;
  message: string;
}

let lastFailure: SilentFailure | undefined;

// Office identity API error codes worth naming; anything else shows the raw
// code. See OfficeRuntime.auth.getAccessToken error reference.
const NAMED_SSO_CODES = [13001, 13002, 13003, 13005, 13008, 13010, 13012];

/**
 * Set when the user's organisation has never approved the add-in (AADSTS65001).
 *
 * This is not something the user can fix by signing in again — an administrator
 * has to approve it once for the whole organisation. Without this the user sees
 * Microsoft's "Need admin approval" screen, returns, and has no idea what to do
 * next, which is a support ticket rather than a self-service action.
 */
let consentTenantId: string | undefined;

export function recordConsentRequired(tenantId?: string): void {
  consentTenantId = tenantId ?? "organizations";
}

export function clearConsentRequired(): void {
  consentTenantId = undefined;
}

/**
 * Link an administrator can follow to approve the add-in for their whole
 * organisation, or undefined if consent is not the problem.
 */
export function getAdminConsentUrl(clientId: string): string | undefined {
  if (!consentTenantId) {
    return undefined;
  }
  return `https://login.microsoftonline.com/${consentTenantId}/adminconsent?client_id=${clientId}`;
}

/**
 * Something the user can act on, for a failed interactive sign-in.
 *
 * Entra errors arrive as raw strings carrying AADSTS codes, trace ids and
 * timestamps. Those are essential in a bug report and meaningless on screen, so
 * the detail goes to the console and the user gets a sentence.
 */
export function describeAuthError(err: unknown): string {
  const raw = (err as Error | undefined)?.message ?? String(err);
  // eslint-disable-next-line no-console
  console.warn("[lox24-auth] sign-in failed", { raw, err });

  // Our own error types already carry a message written for the user, often more
  // specific than anything inferred here. Only translate errors that came from
  // Entra or Office.
  const name = (err as Error | undefined)?.name;
  if (
    name === "CalendarAutomationError" ||
    name === "SettingsSyncError" ||
    name === "Lox24ApiError"
  ) {
    return raw;
  }

  if (raw.includes("AADSTS65004") || raw.includes("consent_required")) {
    return t("auth.error.consentDeclined");
  }
  if (raw.includes("AADSTS65001")) {
    return t("auth.error.notApproved");
  }
  if (/user_cancelled|cancell?ed/i.test(raw)) {
    return t("auth.error.cancelled");
  }
  if (/popup|blocked/i.test(raw)) {
    return t("auth.error.popupBlocked");
  }
  if (/network|fetch|offline/i.test(raw)) {
    return t("auth.error.network");
  }
  return t("auth.error.generic");
}

export function recordSilentFailure(stage: SilentFailureStage, err: unknown): void {
  const code = (err as { code?: number } | undefined)?.code;
  const message = (err as Error | undefined)?.message ?? String(err);
  lastFailure = { stage, code: typeof code === "number" ? code : undefined, message };
  // eslint-disable-next-line no-console
  console.warn(`[lox24-auth] silent restore failed at ${stage}`, { code, message, err });
}

/**
 * Short, user-presentable reason the session could not be restored, or
 * undefined when nothing has failed (e.g. a genuine first sign-in).
 */
export function getSilentFailureSummary(): string | undefined {
  if (!lastFailure) {
    return undefined;
  }
  const { stage, code } = lastFailure;
  if (stage === "sso") {
    const label =
      code && NAMED_SSO_CODES.includes(code) ? t(`auth.sso.${code}`) : t("auth.sso.unavailable");
    return code
      ? t("auth.silent.withCode", { label, code })
      : t("auth.silent.withoutCode", { label });
  }
  if (!isPersistentStorageAvailable()) {
    return t("auth.silent.noStorage");
  }
  return t("auth.silent.generic");
}
